<template>
  <div class="d-flex flex-column h-100">
    <main class="form-signin w-100 m-auto overflow-hidden mel-glass-panel">
      <Form id="login" @submit="onSubmit" :validation-schema="schema" v-slot="{ errors }" class="m-2">
        <ScanInput ref="codeScanInput" :instruction="$t('action.scanToken')" @scanned="codeScanned" :hidden="true" />
        <div class="h3 mb-3 fw-normal">
          <img alt="Icon" src="/icon.svg" class="icon" />
          {{ $t('page.login.heading') }}
        </div>
        <button class="btn btn-primary w-100 py-2" type="button" :disabled="isAuthenticating" @click="onPasskey">
          <span v-show="isWaitingForPasskey" class="spinner-border spinner-border-sm mr-1"></span>
          <font-awesome-icon :icon="['fas', 'user-key']" />
          {{ $t('action.loginWithPasskey') }}
        </button>
        <template v-if="features.entraId">
        <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.or') }}</p>
        </div>
        <button class="btn btn-primary w-100 py-2" type="button" :disabled="isAuthenticating" @click="onEntraLogin">
          <span v-show="isWaitingForEntra" class="spinner-border spinner-border-sm mr-1"></span>
          <font-awesome-icon :icon="['fab', 'microsoft']" />
          {{ $t('action.loginWithMicrosoft') }}
        </button>
        </template>
        <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.or') }}</p>
        </div>
        <div class="form-floating">
          <Field id="email" name="email" type="email" autocomplete="username webauthn" class="form-control"
            placeholder="name@example.com" :class="{ 'is-invalid': errors.email }" required v-focus :disabled="isAuthenticating" />
          <label for="email">{{ $t('field.generic.email') }}</label>
        </div>
        <div class="form-floating">
          <Field id="password" name="password" type="password" autocomplete="current-password" class="form-control"
            placeholder="Password" :class="{ 'is-invalid': errors.password }" required :disabled="isAuthenticating" />
          <label for="password">{{ $t('field.generic.password') }}</label>
        </div>
        <button class="btn btn-primary w-100 py-2" type="submit" :disabled="isAuthenticating">
          <span v-show="isSubmittingForm" class="spinner-border spinner-border-sm mr-1"></span>
          {{ $t('page.login.signin') }}
        </button>
        <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
        <div class="pt-3">
          <button class="btn btn-link p-0" type="button" @click="router.push({ name: 'forgot-password' })" :disabled="isAuthenticating">{{ $t('page.login.forgotPassword') }}</button>
        </div>
      </Form>
    </main>
    <FooterComponent />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { router } from "@/services";
import { Form, Field, GenericObject, useSetFormErrors } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';
import { useAuthStore, useFeaturesStore } from "@/stores";
import { passKeyService } from '@/services/passkey-service';
import { basicApi } from '@/services/api';
import FooterComponent from "@/components/FooterComponent.vue";
import ScanInput from '@/components/form/ScanInput.vue';
import type IEntraAuthorizeResponse from '@/models/responses/IEntraAuthorizeResponse';
import { storeToRefs } from 'pinia';

const featuresStore = useFeaturesStore();
const { data: features } = storeToRefs(featuresStore);

onMounted(async () => {
  await featuresStore.fetchAll();
  startConditionalMediation();
});

onBeforeUnmount(() => {
  passKeyService.abortPendingGet();
});

const i18n = useI18n();
const setFormErrors = useSetFormErrors()

const isSubmittingForm = ref(false);
const isWaitingForPasskey = ref(false);
const isWaitingForEntra = ref(false);
const isWaitingForCode = ref(false);
const isAuthenticating = computed(() =>
  isSubmittingForm.value || isWaitingForPasskey.value || isWaitingForEntra.value || isWaitingForCode.value
);

const schema = Yup.object().shape({
  email: Yup.string().email(i18n.t('validation.emailRequired')).required(i18n.t('validation.emailRequired')).email(i18n.t('validation.emailType')),
  password: Yup.string().required(i18n.t('validation.passwordRequired'))
});

async function onSubmit(values: GenericObject, { setErrors }: GenericObject) {
  isSubmittingForm.value = true;
  const authStore = useAuthStore();
  const { email, password } = values;

  try {
    await authStore.login(email, password);
  } catch (error) {
    return setErrors({ apiError: error });
  } finally {
    isSubmittingForm.value = false;
  }
}

async function onPasskey() {
  isWaitingForPasskey.value = true;
  try {
    const assertionOptions = await passKeyService.createAssertionOptions();
    const assertionVerification = await passKeyService.verifyAssertion(assertionOptions);
    const authStore = useAuthStore();
    await authStore.loginWithToken(assertionVerification);
  } catch (error) {
    return setFormErrors({ apiError: 'api failed' });
  } finally {
    isWaitingForPasskey.value = false;
  }
}

async function startConditionalMediation() {
  if (!await passKeyService.isConditionalMediationAvailable()) return;
  // Only this flow's finally may clear isWaitingForPasskey — if the conditional get is aborted
  // by onPasskey/etc., that flow now owns the waiting flag and we must not clobber it.
  let acquiredWaitingFlag = false;
  try {
    const assertionOptions = await passKeyService.createAssertionOptions();
    const assertionVerification = await passKeyService.verifyAssertion(assertionOptions, true);
    isWaitingForPasskey.value = true;
    acquiredWaitingFlag = true;
    const authStore = useAuthStore();
    await authStore.loginWithToken(assertionVerification);
  } catch {
    // conditional mediation was aborted or failed — silently ignore
  } finally {
    if (acquiredWaitingFlag) {
      isWaitingForPasskey.value = false;
    }
  }
}

const codeScanned = async (barcode: string) => {
  if (isAuthenticating.value) return;
  isWaitingForCode.value = true;
  const authStore = useAuthStore();
  try {
    await authStore.loginWithCodeToken(barcode);
  } catch (error) {
    return setFormErrors({ apiError: 'api failed' });
  } finally {
    isWaitingForCode.value = false;
  }
};

async function onEntraLogin() {
  isWaitingForEntra.value = true;
  try {
    const response = await basicApi.get<IEntraAuthorizeResponse>('v1.0/auth-entra/login-authorize');
    window.location.href = response.data.authorizationUrl;
    // page is navigating away — leave the flag set so controls stay disabled until unmount
  } catch (error) {
    isWaitingForEntra.value = false;
    return setFormErrors({ apiError: 'api failed' });
  }
}
</script>

<style scoped>
.form-signin {
  transition: all 0.4s ease 0s;
  max-width: 348px;
  padding: 1rem;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@media (min-width: 768px) {
  .form-signin {
    left: calc(50% + 280px);
    transform: translate(calc(-50% - 140px), -50%);
  }
}

.form-signin .form-floating:focus-within {
  z-index: 2;
}

.form-signin input[name="email"] {
  margin-bottom: -1px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.form-signin input[name="password"] {
  margin-bottom: 10px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
